import type { DriverNowStatus, VehicleStatus, VehicleType } from "@seagames/contracts";
import { type SVGProps, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { currentLocale, intlLocaleFor } from "../../lib/i18n";
import { WheelchairIcon } from "../transport/icons";
import "../transport/transport.css";
import { formatPhone } from "./model";

/**
 * The fleet's small pieces, shared by its lists, sheets and the driver week
 * — and by S4's dispatch desk (`VehicleTypeIcon`, `useVehicleTypeLabel`).
 * Chips reuse the trip colour tokens of `transport/transport.css`, so a
 * driver "On trip" is the same blue as a trip "En route".
 */

type IconProps = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;

/** Her vehicle glyphs (`l3.js` `UI.P.car / van / bus`): 24-unit box, 1.8 stroke, `currentColor`. */
export function VehicleTypeIcon({ type, ...props }: IconProps & { type: VehicleType }) {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable={false} {...props}>
			{type === "BUS" ? (
				<>
					<rect x="5" y="3.5" width="14" height="14" rx="2.5" />
					<path d="M5 11h14M8 17.5V20M16 17.5V20" />
					<circle cx="8.5" cy="14.3" r=".8" />
					<circle cx="15.5" cy="14.3" r=".8" />
				</>
			) : type === "VAN" ? (
				<>
					<path d="M3.5 16.5V7.5A1.5 1.5 0 0 1 5 6h9.5l4 4.5 2 1.2v4.8Z" />
					<path d="M14.5 6v5.5h6" />
					<circle cx="7.5" cy="17" r="1.6" />
					<circle cx="16.5" cy="17" r="1.6" />
				</>
			) : (
				<>
					<path d="M5 12.5 6.6 8a1.5 1.5 0 0 1 1.4-1h8a1.5 1.5 0 0 1 1.4 1l1.6 4.5" />
					<rect x="3.5" y="12.5" width="17" height="5" rx="1.5" />
					<path d="M6.5 17.5v2M17.5 17.5v2" />
				</>
			)}
		</svg>
	);
}

/** "Private car" / "Van" / "Bus" in the UI language. */
export function useVehicleTypeLabel(): (type: VehicleType) => string {
	const { t } = useTranslation("transportFleet");
	return (type) => t(`vehicleType.${type}`);
}

/** A vehicle type with its glyph: "🚐 Van". */
export function VehicleTypeLabel({ type }: { type: VehicleType }) {
	const label = useVehicleTypeLabel();
	return (
		<span className="inline-flex items-center gap-[0.375rem] whitespace-nowrap text-gms-ink-2">
			<VehicleTypeIcon type={type} className="size-[0.9375rem] shrink-0 text-gms-ink-3" />
			{label(type)}
		</span>
	);
}

/**
 * The vehicle types a driver is certified for, as glyph chips (hers were
 * the English initials C / V / B — a glyph reads the same in all ten
 * languages; the name is in the tooltip and for screen readers).
 */
export function CertifiedTypes({ types }: { types: readonly VehicleType[] }) {
	const label = useVehicleTypeLabel();
	return (
		<span className="inline-flex flex-wrap gap-[0.25rem]">
			{types.map((type) => (
				<span key={type} title={label(type)} className="inline-grid size-[1.375rem] place-items-center rounded-[0.25rem] bg-gms-navy-soft text-gms-navy">
					<VehicleTypeIcon type={type} className="size-[0.875rem]" />
					<span className="sr-only">{label(type)}</span>
				</span>
			))}
		</span>
	);
}

/** Her `.pill` with the wheelchair glyph: the vehicle has a ramp or lift. */
export function RampPill() {
	const { t } = useTranslation("transportFleet");
	return (
		<span className="inline-flex h-[1.25rem] shrink-0 items-center gap-[0.25rem] rounded-[0.25rem] bg-gms-navy-soft px-[0.375rem] text-[0.6875rem] font-semibold whitespace-nowrap text-gms-navy">
			<WheelchairIcon className="size-[0.75rem]" />
			{t("vehicle.ramp")}
		</span>
	);
}

/** Chip tones borrowed from the trip colours (`data-tp-status`). */
const DRIVER_TONE: Record<DriverNowStatus, string> = { ON_TRIP: "EN_ROUTE", ON_DUTY: "ASSIGNED", OFF_DUTY: "CANCELLED", REST: "REJECTED" };
const VEHICLE_TONE: Record<VehicleStatus, string> = { ACTIVE: "ASSIGNED", MAINTENANCE: "REJECTED", RETIRED: "CANCELLED" };

/** Her `DRV_ST` chip: On trip · On duty · Off duty · Rest day. */
export function DriverNowChip({ status }: { status: DriverNowStatus }) {
	const { t } = useTranslation("transportFleet");
	return (
		<span className="tp-st" data-tp-status={DRIVER_TONE[status]}>
			{t(`driverNow.${status}`)}
		</span>
	);
}

export function VehicleStatusChip({ status }: { status: VehicleStatus }) {
	const { t } = useTranslation("transportFleet");
	return (
		<span className="tp-st" data-tp-status={VEHICLE_TONE[status]}>
			{t(`vehicleStatus.${status}`)}
		</span>
	);
}

/** An inactive driver or company: the same chip shape, grey. */
export function InactiveChip() {
	const { t } = useTranslation("transportFleet");
	return (
		<span className="tp-st" data-tp-status="CANCELLED">
			{t("common.inactive")}
		</span>
	);
}

// ── Clock and weekday ───────────────────────────────────────────────────────

/**
 * `"07:00"` → `"7:00AM"`: the platform's clock shape (`formatClock`), for a
 * wall time that has no date (a shift). Unreadable input comes back as is.
 */
export function wallClock(hhmm: string): string {
	const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
	if (!m) return hhmm;
	const h24 = Number(m[1]);
	const h = h24 % 12 === 0 ? 12 : h24 % 12;
	return `${h}:${m[2]}${h24 < 12 ? "AM" : "PM"}`;
}

/** A shift that ends at or before it starts runs past midnight. */
export const isOvernight = (start: string, end: string): boolean => end.slice(0, 5) <= start.slice(0, 5);

/** "7:00AM–7:00PM", plus "overnight" under it when it runs past midnight. */
export function ShiftText({ start, end }: { start: string; end: string }) {
	const { t } = useTranslation("transportFleet");
	return (
		<span className="grid whitespace-nowrap tabular-nums">
			<span>{t("shift.range", { from: wallClock(start), to: wallClock(end) })}</span>
			{isOvernight(start, end) && <span className="text-[0.75rem] text-gms-ink-3">{t("shift.overnight")}</span>}
		</span>
	);
}

/**
 * ISO weekday (1 = Monday … 7 = Sunday) → its name in the UI language, from
 * Intl (1 Jan 2024 was a Monday). `bm` / `tet` go through `intlLocaleFor`.
 */
export function useWeekdayName(): (isodow: number, width?: "long" | "short") => string {
	const locale = intlLocaleFor(currentLocale());
	return useMemo(() => {
		const long = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" });
		const short = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
		return (isodow: number, width: "long" | "short" = "long") => (width === "short" ? short : long).format(new Date(Date.UTC(2024, 0, isodow)));
	}, [locale]);
}

/** The ISO weekdays Monday-first, for a rest-day picker. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

// ── Phone ───────────────────────────────────────────────────────────────────

/** A phone number as a `tel:` link (a driver or a group lead is called from the list). */
export function PhoneLink({ phone, callingCodes, className = "" }: { phone: string; callingCodes: readonly string[]; className?: string }) {
	return (
		<a href={`tel:${phone}`} className={`whitespace-nowrap tabular-nums text-gms-ink-2 underline-offset-2 hover:text-gms-navy hover:underline ${className}`}>
			{formatPhone(phone, callingCodes)}
		</a>
	);
}
